import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { ColorPicker, ColorPickerProps, Tooltip } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Spacing } from '../theme/Spacing';
import { AtiraIcon } from './AtiraIcon';
import { Flex } from './Flex';
import { Text } from './Text';

const StyledWrapper = styled(Flex)`
  gap: ${Spacing.s};
  flex-direction: column;
`;

const StyledColorPicker = styled(ColorPicker).attrs((props) => ({
  'data-testid': props['data-testid'],
}))<{ valid?: boolean }>`
  width: 2.3rem;
  height: 2.3rem;

  ${({ theme, valid }) =>
    !valid ? `border-color: ${theme.danger} !important;` : ''}

  .ant-color-picker-color-block,
  .ant-color-picker-clear {
    height: 100% !important;
    width: 100% !important;
  }
`;

const InfoSpan = styled.span`
  display: inline-flex;
`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.fgMuted};
  display: inline-block;
  margin-inline-start: ${Spacing.s};
`;

const StyledLabel = styled.label`
  width: max-content;
`;

interface AtiraColorPickerProps extends ColorPickerProps {
  title?: string;
  className?: string;
  valid?: boolean;
  errorMessage?: string;
  info?: string;
  required?: boolean;
  dataTestId?: string;
}

export const AtiraColorPicker: React.FC<AtiraColorPickerProps> = ({
  title,
  valid = true,
  errorMessage,
  info,
  required,
  dataTestId,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <StyledWrapper>
      {title ? (
        <StyledLabel>
          {info ? (
            <Tooltip title={info}>
              <InfoSpan>
                <AtiraIcon icon={faInfoCircle} color="main" />
              </InfoSpan>
            </Tooltip>
          ) : null}
          <span>{title}</span>
          {!required ? (
            <OptionalSpan>({t('common.optional')})</OptionalSpan>
          ) : null}
        </StyledLabel>
      ) : null}

      <StyledColorPicker {...props} valid={valid} data-testId={dataTestId} />

      {errorMessage && !valid ? (
        <Text fontSize="s" color="danger">
          {errorMessage}
        </Text>
      ) : null}
    </StyledWrapper>
  );
};
